import { useEffect, useMemo, useState } from "react";
import { createPopupLead } from "../lib/storage";

const DONT_SHOW_KEY = "dreameer_popup_hide_until";
const WA_PHONE = "919507505612";

const testimonials = [
  {
    img: "https://randomuser.me/api/portraits/women/44.jpg",
    text: '"Recorded classes and mock tests helped me score 92%." — Ananya, Class 12',
  },
  {
    img: "https://randomuser.me/api/portraits/men/32.jpg",
    text: '"Excellent doubt clearing and patient tutors." — Aman, Class 11',
  },
  {
    img: "https://randomuser.me/api/portraits/women/68.jpg",
    text: '"Great value, flexible timings, and verified teachers." — Pooja, Parent',
  },
];

const classOptions = [
  "Class 7",
  "Class 8",
  "Class 9",
  "Class 10",
  "Class 11 (Science)",
  "Class 11 (Commerce)",
  "Class 12 (Science)",
  "NEET / JEE Foundation",
  "CUET / UG",
  "Skill Course (ADCA)",
];

const isValidPhone = (value) => /^[6-9]\d{9}$/.test(value || "");

export default function LeadPopup() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", goal: "" });
  const [testiIndex, setTestiIndex] = useState(0);

  const activeTestimonial = useMemo(() => testimonials[testiIndex], [testiIndex]);

  const allowedToShow = () => {
    const until = window.localStorage.getItem(DONT_SHOW_KEY);
    if (!until) {
      return true;
    }
    return Date.now() > Number(until);
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const showTimer = window.setTimeout(() => {
      if (allowedToShow()) {
        setOpen(true);
        setMinimized(false);
      }
    }, 700);

    const interval = window.setInterval(() => {
      setTestiIndex((current) => (current + 1) % testimonials.length);
    }, 4200);

    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
      if (event.key === "m" || event.key === "M") {
        setOpen(false);
        setMinimized(true);
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.clearTimeout(showTimer);
      window.clearInterval(interval);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");
    const name = form.name.trim();
    const phone = (form.phone || "").replace(/\D/g, "");
    const goal = form.goal;

    if (!name) {
      setError("Please enter the student name.");
      return;
    }
    if (!isValidPhone(phone)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!goal) {
      setError("Please select a class or goal.");
      return;
    }

    createPopupLead({
      studentName: name,
      mobile: phone,
      targetExam: goal,
      source: "popup",
    });

    setSuccess(true);
    const until = Date.now() + 24 * 60 * 60 * 1000;
    window.localStorage.setItem(DONT_SHOW_KEY, String(until));
  };

  const openWhatsApp = () => {
    const phone = (form.phone || "").replace(/\D/g, "");
    const message = `Hi Dreameer! I want a free demo. Student: ${form.name || ""} | Mobile: ${phone || ""} | Class/Goal: ${form.goal || ""}`;
    const url = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  if (!open && !minimized) {
    return null;
  }

  return (
    <>
      {open ? (
        <div
          className="hc-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hcTitle"
          aria-describedby="hcDesc"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setOpen(false);
              setMinimized(true);
            }
          }}
        >
          <div className="hc-card" role="document">
            <div className="hc-controls">
              <button
                type="button"
                className="hc-ctrl-btn"
                aria-label="Don't show for 24 hours"
                onClick={() => {
                  const until = Date.now() + 24 * 60 * 60 * 1000;
                  window.localStorage.setItem(DONT_SHOW_KEY, String(until));
                  setOpen(false);
                }}
              >
                24h
              </button>
              <button
                type="button"
                className="hc-ctrl-btn"
                aria-label="Minimize popup"
                onClick={() => {
                  setOpen(false);
                  setMinimized(true);
                }}
              >
                _
              </button>
              <button
                type="button"
                className="hc-ctrl-btn"
                aria-label="Close popup"
                onClick={() => setOpen(false)}
              >
                x
              </button>
            </div>

            <div className="hc-hero">
              <span className="hc-badge">Classes 7-12 | Admission and Skill Courses</span>
              <h1 id="hcTitle" className="hc-title">
                Book a Free Demo and Get Admission Help
              </h1>
              <p id="hcDesc" className="hc-sub">
                Free 30-minute diagnostic, personalized study plan, verified tutors, recorded lessons, and parent
                progress updates. Limited slots available.
              </p>

              <div className="hc-stats">
                <div className="stat">
                  <div>10K+</div>
                  <div>
                    <strong>Students</strong>
                    <small>Happy learners</small>
                  </div>
                </div>
                <div className="stat">
                  <div>4.8/5</div>
                  <div>
                    <strong>Rating</strong>
                    <small>Average score</small>
                  </div>
                </div>
                <div className="stat">
                  <div>500+</div>
                  <div>
                    <strong>Tutors</strong>
                    <small>Verified experts</small>
                  </div>
                </div>
              </div>

              <div className="hc-benefits">
                <div className="benefit">
                  <div className="icon">1</div>
                  <div>Board-aligned lessons and mock tests</div>
                </div>
                <div className="benefit">
                  <div className="icon">2</div>
                  <div>Expert, verified tutors for every subject</div>
                </div>
                <div className="benefit">
                  <div className="icon">3</div>
                  <div>Recorded sessions for fast revision</div>
                </div>
                <div className="benefit">
                  <div className="icon">4</div>
                  <div>Flexible payments and affordable batches</div>
                </div>
              </div>
            </div>

            <div className="hc-form" aria-live="polite">
              {!success ? (
                <>
                  <div>
                    <h3>Reserve your Free Demo slot</h3>
                    <p>Get a callback within 24 hours. Slots fill quickly.</p>
                  </div>

                  <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                    <div className="form-row">
                      <input
                        id="popup-name"
                        name="name"
                        className="form-field"
                        placeholder="Student name"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-row">
                      <input
                        id="popup-phone"
                        name="phone"
                        className="form-field"
                        placeholder="Mobile number (10 digits)"
                        inputMode="numeric"
                        value={form.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-row">
                      <select
                        id="popup-goal"
                        name="goal"
                        className="form-field"
                        value={form.goal}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select class / goal</option>
                        {classOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    {error ? <div className="hc-error">{error}</div> : null}

                    <button type="submit" className="cta-primary">
                      Book Free Demo
                    </button>

                    <div className="cta-row">
                      <a className="cta-ghost" href="tel:+919507505612">
                        Call +91 95075 05612
                      </a>
                      <button type="button" className="cta-wa" onClick={openWhatsApp}>
                        WhatsApp
                      </button>
                    </div>

                    <div className="small-note">No spam. Our counselor will call to confirm your demo.</div>

                    <button
                      type="button"
                      className="cta-ghost hc-mobile-close"
                      onClick={() => setOpen(false)}
                    >
                      Close
                    </button>
                  </form>
                </>
              ) : (
                <div className="hc-success" role="status">
                  <div className="hc-success-title">Thanks. Request received.</div>
                  <div className="hc-success-sub">
                    Our team will call you within 24 hours to schedule the demo session.
                  </div>
                  <button
                    type="button"
                    className="cta-primary"
                    onClick={() => {
                      setOpen(false);
                      setMinimized(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              )}

              <div className="testimonials" aria-hidden="false">
                <img src={activeTestimonial.img} alt="Student testimonial" />
                <div>{activeTestimonial.text}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {minimized ? (
        <button
          type="button"
          className="hc-pill"
          onClick={() => {
            if (allowedToShow()) {
              setOpen(true);
              setMinimized(false);
            }
          }}
        >
          Book Demo
        </button>
      ) : null}
    </>
  );
}
